import { encodeName } from '@/common/utils/client';
import { useSelector } from 'react-redux';
import { useCallback, useState, useRef } from 'react';
import { AppState, useAppDispatch } from '../index';
import { setVoteList, setVoteInfo, setVoteAmount, setMyVoteInfo, setMyVoteList, setRefundVoteList } from './actions';
import { useLogin } from '@/state/base/hooks';
import { useSleep } from '@/hooks';
import Server from 'utils/serviceApi';
import { VoteListParams } from 'utils/serviceApi/typing';
import BigNumber from 'bignumber.js';

export function useVoteList(): [any[], (query: Record<string, any>, is_frist_query: boolean, is_reset_state?: boolean) => Promise<any>, boolean] {
    const dispatch = useAppDispatch();
    // const totalPages = useRef(0);
    const paramsRef = useRef<Partial<VoteListParams>>({
        sort: '',
        noteName: '',
    });
    const [loadding, setLoadding] = useState<boolean>(false);
    const voteList = useSelector<AppState, AppState['vote']['voteList']>((state: AppState) => state.vote.voteList);
    const getVoteList = useCallback(
        async (query: Record<string, any> = {}, is_frist_query: boolean = true, is_reset_state: boolean = true) => {
            try {
                is_reset_state && setLoadding(true);
                let _params: Partial<VoteListParams> = {};
                if (is_frist_query) {
                    _params = {
                        sort: '',
                        noteName: '',
                        ...query,
                    };
                    is_reset_state && dispatch(setVoteList({ data: [], is_frist: true }));
                } else {
                    _params = { ...paramsRef.current, ...query };
                }
                const data: any = await Server.getProduceList(_params);
                if (data.code === 200) {
                    paramsRef.current = _params;
                    const list = data.data.map(ele => {
                        const issuancePlan = (ele.issuancePlan ?? '').replace(/cn:/, '').split('|en:');
                        const mainifesto = (ele.mainifesto ?? '').replace(/cn:/, '').split('|en:');
                        const orgName = (ele.orgName ?? '').replace(/cn:/, '').split('|en:');
                        const tokenPlan = (ele.tokenPlan ?? '').replace(/cn:/, '').split('|en:');
                        return { ...ele, electedVotes: ele.electedVotes, tokenPlanCN: tokenPlan[0], tokenPlanEN: tokenPlan[1], issuancePlanCN: issuancePlan[0], issuancePlanEN: issuancePlan[1], mainifestoCN: mainifesto[0], mainifestoEN: mainifesto[1], orgNameCN: orgName[0], orgNameEN: orgName[1] };
                    });
                    dispatch(setVoteList({ data: list, is_frist: true }));
                } else {
                    throw new Error(data.msg);
                }
            } catch (e: any) {
                console.error('Get SwapOrderList error:', e.message);
                window.toast.error(e.message);
            } finally {
                setLoadding(false);
            }
        },
        [dispatch]
    );
    return [voteList, getVoteList, loadding];
}

export function useVoteInfo(): [Record<string, any>, (info: Record<string, any>) => void] {
    const dispatch = useAppDispatch();
    const voteInfo = useSelector<AppState, AppState['vote']['voteInfo']>((state: AppState) => state.vote.voteInfo);
    const handVoteInfo = (info: Record<string, any>) => {
        dispatch(setVoteInfo(info));
    };
    return [voteInfo, handVoteInfo];
}

export function useVoteAmount(): [string, (amount: string | number) => void] {
    const dispatch = useAppDispatch();
    const voteAmount = useSelector<AppState, AppState['vote']['voteAmount']>((state: AppState) => state.vote.voteAmount);
    const handVoteAmount = (amount: string | number) => {
        dispatch(setVoteAmount(amount));
    };
    return [voteAmount, handVoteAmount];
}

export function useVote(): [{ handVote: (producers: string[], isChangeProducers: boolean) => void; handReVote: (producers: string[]) => void; handSubVote: (votes: string | number) => void; handReward: () => void }, boolean] {
    const [userInfo] = useLogin();
    const [voteAmount] = useVoteAmount();
    const [loadding, setLoadding] = useState<boolean>(false);
    const handVote = useCallback(
        async (producers: string[], isChangeProducers:boolean) => {
            try {
                setLoadding(true);
                producers.sort(function (a, b) {
                    a = encodeName(a);
                    b = encodeName(b);
                    if (/\d/.test(a) && /\d/.test(b)) {
                      // return parseInt(a) - parseInt(b);
                      return new BigNumber(a).isLessThan(b) ? -1 : 1;
                    } else if (/\d/.test(a)) {
                        return -1;
                    } else if (/\d/.test(b)) {
                        return 1;
                    } else {
                        return a.localeCompare(b);
                    }
                });
                await Server.vote(userInfo.name, voteAmount, producers, isChangeProducers);
                return true;
            } catch (e) {
                console.log('error--');
                
                throw e;
            } finally {
                setLoadding(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [userInfo.name]
    );

    const handReVote = useCallback(
        async (producers: string[]) => {
            try {
                setLoadding(true);
                producers.sort(function (a, b) {
                    a = encodeName(a);
                    b = encodeName(b);
                    if (/\d/.test(a) && /\d/.test(b)) {
                        // return parseInt(a) - parseInt(b);
                        return new BigNumber(a).isLessThan(b) ? -1 : 1;
                    } else if (/\d/.test(a)) {
                        return -1;
                    } else if (/\d/.test(b)) {
                        return 1;
                    } else {
                        return a.localeCompare(b);
                    }
                });
                await Server.reVote(userInfo.name, producers);
                return true;
            } catch (e) {
                throw e;
            } finally {
                setLoadding(false);
            }
        },
        [userInfo.name]
    );

    const handSubVote = useCallback(
        async (votes: string | number) => {
            try {
                setLoadding(true);
                await Server.subVote(userInfo.name, votes);
                return true;
            } catch (e) {
                throw e;
            } finally {
                setLoadding(false);
            }
        },
        [userInfo.name]
    );

    const handReward = useCallback(async () => {
        try {
            setLoadding(true);
            await Server.reward(userInfo.name);
            return true;
        } catch (e) {
            throw e;
        } finally {
            setLoadding(false);
        }
    }, [userInfo.name]);

    return [{ handVote, handReVote, handSubVote, handReward }, loadding];
}

export function useAddVote(): [(amount: string | number) => void, boolean] {
    const [userInfo] = useLogin();
    const [loadding, setLoadding] = useState<boolean>(false);
    const addVote = useCallback(
        async (amount: string | number) => {
            try {
                setLoadding(true);
                await Server.addVote(userInfo.name, amount);
                return true;
            } catch (e) {
                throw e;
            } finally {
                setLoadding(false);
            }
        },
        [userInfo.name]
    );
    return [addVote, loadding];
}

export function useVoteByUser(): [{ voteList: any[]; refundVoteList: any[] }, { produceListByUser: (isDelay?: boolean) => void }, boolean] {
    const dispatch = useAppDispatch();
    const [userInfo] = useLogin();
    const [sleep] = useSleep();
    const [loadding, setLoadding] = useState<boolean>(true);
    const myVoteList = useSelector<AppState, AppState['vote']['myVoteList']>((state: AppState) => state.vote.myVoteList);
    const refundVoteList = useSelector<AppState, AppState['vote']['refundVoteList']>((state: AppState) => state.vote.refundVoteList);

    const produceListByUser = useCallback(
        async (isDelay: boolean = false) => {
            try {
                setLoadding(true);
                isDelay && (await sleep(2000));
                const result: any = await Server.getProduceListByUser(userInfo.name);
                // const result: any = await Server.getProduceListByUser('jd2');
                if (result.code === 200) {
                    const { voteInfos, producerVO, voteRefundInfos } = result.data;
                    let _myVoteList = [];
                    if (voteInfos.lastUnvotedTime) {
                        _myVoteList = [
                            {
                                votes: voteInfos.votes,
                                isRefund: false,
                                tokenNum: voteInfos.tokenNum,
                                time: (new Date(voteInfos.lastUnvotedTime * 1000) as any).format('yyyy/MM/dd hh:mm:ss'),
                                producerVOS: producerVO.map(ele => {
                                    const org_name = (ele.org_name ?? '').replace(/cn:/, '').split('|en:');
                                    return {
                                        ...ele,
                                        org_name_zh: org_name[0],
                                        org_name_en: org_name[1],
                                    };
                                }),
                            },
                        ];
                    } else {
                        _myVoteList = [];
                    }
                    // const _refundVoteList = voteRefundInfos.map(item => ({ ...item, votes: item.voteNum, isRefund: true, producerVOS: item.logoUrl.map(val => ({ logo_uri: val })), time: (new Date(item.votedTime * 1000) as any).format('yyyy/MM/dd hh:mm:ss') }));
                    const _refundVoteList = voteRefundInfos.map(item => ({
                        ...item,
                        votes: item.voteNum,
                        isRefund: true,
                        producerVOS: item.producerList.map(ele => {
                            const org_name = (ele.org_name ?? '').replace(/cn:/, '').split('|en:');
                            return {
                                ...ele,
                                org_name_zh: org_name[0],
                                org_name_en: org_name[1],
                            };
                        }),
                        time: (new Date(item.votedTime * 1000) as any).format('yyyy/MM/dd hh:mm:ss'),
                    }));
                    dispatch(setMyVoteList(_myVoteList));
                    dispatch(setRefundVoteList(_refundVoteList));
                } else {
                    throw new Error(result.msg);
                }
                return result.data;
            } catch (e: any) {
                console.error('Get VoteByUser error:', e.message);
                window.toast.error(e.message);
            } finally {
                setLoadding(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [userInfo.name]
    );

    return [{ voteList: myVoteList, refundVoteList }, { produceListByUser }, loadding];
}

export function useMyVoteInfo(): [Record<string, any>, () => void, boolean] {
    const [userInfo] = useLogin();
    const dispatch = useAppDispatch();
    const [loadding, setLoadding] = useState<boolean>(false);

    const myVoteInfo = useSelector<AppState, AppState['vote']['myVoteInfo']>((state: AppState) => state.vote.myVoteInfo);

    const getMyVoteInfo = useCallback(async () => {
        try {
            setLoadding(true);
            const result: any = await Server.getMyVoteInfo(userInfo.name);
            if (result.code === 200) {
                dispatch(setMyVoteInfo({...result.data, producerVo: result.data?.producerVo ?? []}));
            } else {
                throw new Error(result.msg);
            }
            return result.data;
        } catch (e: any) {
            console.error('Get VoteByUser error:', e.message);
            window.toast.error(e.message);
        } finally {
            setLoadding(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo.name]);

    return [myVoteInfo, getMyVoteInfo, loadding];
}
